import * as Effect from "effect-app/Effect"
import * as S from "effect-app/Schema"
import type * as Schema from "effect/Schema"
import * as Struct from "effect/Struct"
import { ShopFromId, ShopId, SwissMarket } from "../Shop.ts"
import { type HasTimestamps, TimestampProps } from "../Timestamps.ts"
import { CategoryId, ConfiguratorCategoryFromId } from "./Category.ts"
import { ConfiguratorConditionGroup } from "./Condition.ts"
import { DXFConfig } from "./DXFConfig.ts"
import { ConfiguratorOption } from "./Option.ts"
import { OptionGroupId, PriceId, SKU } from "./shared.ts"
import { ShowerPreviewer } from "./ShowerPreviewer.ts"
import { ConfiguratorTagId } from "./Tag.ts"

export const ListOptionItemImage = S.Struct({
  listOptionItemId: S.StringId,
  imageUrl: S.NonEmptyString255
})
export type ListOptionItemImage = typeof ListOptionItemImage.Type

export const ConfiguratorId = S.StringId
export type ConfiguratorId = S.StringId

export class ConfiguratorShopOption extends S.Opaque<ConfiguratorShopOption, ConfiguratorShopOption.Encoded>()(
  S
    .Struct({
      id: S.StringId.withConstructorDefault,
      optionId: S.StringId,
      magentoOptionId: S.PositiveInt,
      ...TimestampProps
    })
    .annotate({ identifier: "ConfiguratorShopOption" })
) implements HasTimestamps {}

export class ConfiguratorShopBase extends S.Opaque<ConfiguratorShopBase, ConfiguratorShopBase.Encoded>()(
  S
    .Struct({
      id: S.StringId.withConstructorDefault,
      shopId: ShopId, // TODO: if shop is deleted, delete entry here
      magentoSku: SKU,
      externalSku: S.NullOr(SKU),
      syncEnabled: S.Boolean.withConstructorDefault,
      options: S.Array(ConfiguratorShopOption).withConstructorDefault, // TODO: if option is deleted, delete entry here.
      syncedConfiguratorVersion: S.NullOr(S.Date).withConstructorDefault,
      syncedAt: S.NullOr(S.Date).withConstructorDefault
    })
    .annotate({ identifier: "ConfiguratorShopBase" })
) {}

export class ConfiguratorShop extends S.Opaque<ConfiguratorShop, ConfiguratorShop.Encoded>()(
  S
    .Struct({
      ...Struct.omit(ConfiguratorShopBase.fields, ["shopId"]),
      shop: ShopFromId // TODO: if shop is deleted, delete entry here // TODO: v4 migration - was fromKey("shopId"), need encodeKeys at schema level
    })
    .annotate({ identifier: "ConfiguratorShop" })
) {
  static readonly Codec = this
    .pipe(S.encodeKeys({ shop: "shopId" }), S.revealCodec)
}

export class ConfiguratorPriceTableItem
  extends S.Opaque<ConfiguratorPriceTableItem, ConfiguratorPriceTableItem.Encoded>()(
    S
      .Struct({
        id: S.StringId.withConstructorDefault,
        row: S.NullOr(S.NonEmptyString255).withConstructorDefault,
        column: S.NullOr(S.NonEmptyString255).withConstructorDefault,
        price: S.NonNegativeDecimal.withConstructorDefault,
        swissPrice: S.NullOr(S.NonNegativeDecimal).withConstructorDefault,
        // TODO: rowSortOrder is bad.
        rowSortOrder: S.Finite.pipe(S.withConstructorDefault(Effect.succeed(0))),
        columnSortOrder: S.Finite.pipe(S.withConstructorDefault(Effect.succeed(0))),
        ...TimestampProps
      })
      .annotate({ identifier: "ConfiguratorPriceTableItem" })
  )
  implements HasTimestamps
{}

// ConfiguratorPriceTableItems ??

const useClosestValue = S.NullOr(S.Literals(["Next", "Previous"]))

export type UseClosestValue = typeof useClosestValue.Type

const navigationBehavior = S.NullOr(S.Literals(["blocking", "permissive"]))

export type NavigationBehavior = typeof navigationBehavior.Type

export class ConfiguratorPriceTableConfig
  extends S.Opaque<ConfiguratorPriceTableConfig, ConfiguratorPriceTableConfig.Encoded>()(
    S
      .Struct({
        optionId: S.StringId,
        useClosestValue: useClosestValue.pipe(S.withConstructorDefault(Effect.succeed("Next" as const)))
      })
      .annotate({ identifier: "ConfiguratorPriceTableConfig" })
  )
{}

export class ConfiguratorPriceTable extends S.Opaque<ConfiguratorPriceTable, ConfiguratorPriceTable.Encoded>()(
  S
    .Struct({
      id: S.StringId.withConstructorDefault,
      items: S.Array(ConfiguratorPriceTableItem).withConstructorDefault,
      name: S.NonEmptyString255,

      columns: S.NullOr(ConfiguratorPriceTableConfig).withConstructorDefault,
      rows: S.NullOr(ConfiguratorPriceTableConfig).withConstructorDefault,

      ...TimestampProps
    })
    .annotate({ identifier: "ConfiguratorPriceTable" })
) implements HasTimestamps {}

export class ConfiguratorOptionGroup extends S.Opaque<ConfiguratorOptionGroup, ConfiguratorOptionGroup.Encoded>()(
  S
    .Struct({
      id: OptionGroupId.withConstructorDefault,
      name: S.NonEmptyString255,
      isOptional: S.Boolean,
      options: S.Array(ConfiguratorOption).withConstructorDefault,
      ...TimestampProps
    })
    .annotate({ identifier: "ConfiguratorOptionGroup" })
) implements HasTimestamps {}

export class ImageTemplate extends S.Opaque<ImageTemplate, ImageTemplate.Encoded>()(
  S
    .Struct({
      optionCode: S.NonEmptyString2k
    })
    .annotate({ identifier: "ImageTemplate" })
) {}

export class ConfiguratorBase extends S.Opaque<ConfiguratorBase, ConfiguratorBase.Encoded>()(
  S
    .Struct({
      id: ConfiguratorId.withConstructorDefault,
      categoryId: S.NullOr(CategoryId).withConstructorDefault,
      isEnabled: S.Boolean.withConstructorDefault,
      title: S.NonEmptyString255,
      showerPreviewer: S.NullOr(ShowerPreviewer).withConstructorDefault,
      dxfConfig: S.NullOr(DXFConfig).withConstructorDefault,
      // so far seems to be always 0?
      basePrice: S.NonNegativeDecimal.withConstructorDefault,
      priceId: PriceId,
      sku: S.NullOr(SKU).withConstructorDefault, // 55{farbe} etc
      sortOrder: S.NonNegativeInt, // default to count(Configurator)?
      // version: S.NonEmptyString255 // so far always null ?
      imageTemplate: S.NullOr(ImageTemplate).withConstructorDefault,
      navigationBehavior: navigationBehavior.withConstructorDefault,
      shops: S.Array(ConfiguratorShopBase).withConstructorDefault, // TODO: remove element if Shop is deleted
      optionGroups: S.Array(ConfiguratorOptionGroup).withConstructorDefault,
      conditionGroups: S.Array(ConfiguratorConditionGroup).withConstructorDefault,
      priceTables: S.Array(ConfiguratorPriceTable).withConstructorDefault, // TODO - can this be managed better with row vs column option relationship?
      swissMarket: S.NullOr(SwissMarket).withConstructorDefault,
      // last time an operator edited the configurator content; unlike timestamps.updatedAt it does not drive shop sync
      lastEditedAt: S.inputDate.pipe(S.withConstructorDefault(Effect.sync(() => new Date()))),
      // operator-assigned labels; metadata only, so changing them must not touch timestamps.updatedAt or lastEditedAt
      tagIds: S.Array(ConfiguratorTagId).withConstructorDefault,
      ...TimestampProps
    })
    .annotate({ identifier: "ConfiguratorBase" })
) implements HasTimestamps {}

export class Configurator extends S.Opaque<Configurator, Configurator.Encoded>()(
  S
    .Struct({
      ...Struct.omit(ConfiguratorBase.fields, ["categoryId", "shops"]),
      category: S
        .NullOr(ConfiguratorCategoryFromId)
        .withConstructorDefault, // TODO: v4 migration - was .pipe(S.fromKey("categoryId")), need encodeKeys
      shops: S.Array(ConfiguratorShop.Codec).withConstructorDefault // TODO: remove element if Shop is deleted
    })
    .annotate({ identifier: "Configurator" })
) implements HasTimestamps {
  static readonly Codec = this
    .pipe(
      S.encodeKeys({ category: "categoryId" }),
      S.revealCodec
    )
}

export namespace ConfiguratorShop {
  export namespace Codec {
    export type Encoded = Schema.Codec.Encoded<typeof ConfiguratorShop.Codec>
  }
}

export namespace Configurator {
  export namespace Codec {
    export type Encoded = Schema.Codec.Encoded<typeof Configurator.Codec>
  }
}

// codegen:start {preset: model}
//
export namespace ConfiguratorShopOption {
  export interface Encoded extends S.StructNestedEncoded<typeof ConfiguratorShopOption> {}
}
export namespace ConfiguratorShopBase {
  export interface Encoded extends S.StructNestedEncoded<typeof ConfiguratorShopBase> {}
}
export namespace ConfiguratorShop {
  export interface Encoded extends S.StructNestedEncoded<typeof ConfiguratorShop> {}
}
export namespace ConfiguratorPriceTableItem {
  export interface Encoded extends S.StructNestedEncoded<typeof ConfiguratorPriceTableItem> {}
}
export namespace ConfiguratorPriceTableConfig {
  export interface Encoded extends S.StructNestedEncoded<typeof ConfiguratorPriceTableConfig> {}
}
export namespace ConfiguratorPriceTable {
  export interface Encoded extends S.StructNestedEncoded<typeof ConfiguratorPriceTable> {}
}
export namespace ConfiguratorOptionGroup {
  export interface Encoded extends S.StructNestedEncoded<typeof ConfiguratorOptionGroup> {}
}
export namespace ImageTemplate {
  export interface Encoded extends S.StructNestedEncoded<typeof ImageTemplate> {}
}
export namespace ConfiguratorBase {
  export interface Encoded extends S.StructNestedEncoded<typeof ConfiguratorBase> {}
}
export namespace Configurator {
  export interface Encoded extends S.StructNestedEncoded<typeof Configurator> {}
}
//
// codegen:end
